import { useCallback, useEffect, useRef, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../state/store';
import { AdminRole } from '../../types/constants.types';
import {
  completeOwnerTour,
  dismissOwnerTour,
  getOwnerTour,
  restartOwnerTour,
  saveOwnerTourProgress,
} from '../../services/OwnerTourService';
import TourTooltip from './TourTooltip';
import { waitForOwnerSetupGate } from '../OwnerSetup/ownerSetupEvents';
import { OWNER_TOUR_START_EVENT, OWNER_TOUR_STEPS } from './tourSteps';

const LAST_STEP = OWNER_TOUR_STEPS.length - 1;
const AUTO_START_DELAY_MS = 900;
const TARGET_WAIT_MS = 8000;

function waitForElement(selector: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element && element.getBoundingClientRect().width > 0) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(check, 120);
    };
    check();
  });
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function OwnerTourRunner() {
  const navigate = useNavigate();
  // useNavigate returns a new function on every location change; keep the tour logic independent of that.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const seenSteps = useRef<Set<number>>(new Set());
  const finished = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const showStep = useCallback(
    async (requested: number) => {
      let index = Math.max(0, requested);
      setRun(false);

      while (index <= LAST_STEP) {
        const step = OWNER_TOUR_STEPS[index];
        const route = step.data?.route;
        if (route && window.location.pathname !== route) navigateRef.current(route);

        const found = step.target === 'body' || (await waitForElement(String(step.target), TARGET_WAIT_MS));
        if (!mounted.current) return;
        if (found) break;
        index += 1; // A missing target must never trap the owner; move on to the next step.
      }

      if (index > LAST_STEP) {
        finished.current = true;
        completeOwnerTour().catch(() => undefined);
        return;
      }

      seenSteps.current.add(index);
      saveOwnerTourProgress(index, Array.from(seenSteps.current)).catch(() => undefined);
      setStepIndex(index);
      setRun(true);
    },
    [],
  );

  // Resume (or start) automatically on login until the owner finishes the tour.
  useEffect(() => {
    let cancelled = false;
    // The mandatory channel setup goes first; the tour starts once that is complete.
    const timer = window.setTimeout(async () => {
      await waitForOwnerSetupGate();
      if (cancelled) return;
      getOwnerTour()
        .then((state) => {
          if (cancelled || !state.shouldAutoStart) return;
          seenSteps.current = new Set(state.completedSteps);
          finished.current = false;
          void showStep(Math.min(state.currentStep, LAST_STEP));
        })
        .catch(() => undefined); // The tour is an enhancement; never get in the way of the dashboard.
    }, AUTO_START_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [showStep]);

  // "Product tour" in the menu replays it on demand.
  useEffect(() => {
    const replay = () => {
      restartOwnerTour()
        .then(() => {
          seenSteps.current = new Set();
          finished.current = false;
          void showStep(0);
        })
        .catch(() => undefined);
    };
    window.addEventListener(OWNER_TOUR_START_EVENT, replay);
    return () => window.removeEventListener(OWNER_TOUR_START_EVENT, replay);
  }, [showStep]);

  const endTour = (outcome: 'complete' | 'dismiss') => {
    if (finished.current) return;
    finished.current = true;
    setRun(false);
    (outcome === 'complete' ? completeOwnerTour() : dismissOwnerTour()).catch(() => undefined);
  };

  const handleCallback = ({ action, index, type, status }: CallBackProps) => {
    if (finished.current) return;

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        if (index >= LAST_STEP) {
          endTour('complete');
        } else {
          void showStep(index + 1);
        }
      } else if (action === ACTIONS.PREV) {
        void showStep(index - 1);
      } else if (action === ACTIONS.CLOSE) {
        endTour(index >= LAST_STEP ? 'complete' : 'dismiss');
      }
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      void showStep(index + 1);
      return;
    }

    if (status === STATUS.SKIPPED || action === ACTIONS.SKIP) {
      endTour(index >= LAST_STEP ? 'complete' : 'dismiss');
    }
  };

  return (
    <Joyride
      steps={OWNER_TOUR_STEPS}
      run={run}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      disableOverlayClose
      spotlightPadding={8}
      scrollOffset={96}
      callback={handleCallback}
      tooltipComponent={TourTooltip}
      floaterProps={{ disableAnimation: prefersReducedMotion() }}
      styles={{
        options: { zIndex: 10000, arrowColor: '#ffffff', overlayColor: 'rgba(16, 24, 40, 0.55)' },
        spotlight: { borderRadius: 16 },
      }}
      locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip for now' }}
    />
  );
}

/** Mounted for every signed-in user; only channel owners ever see (or request) the tour. */
export default function OwnerTour() {
  const role = useSelector((state: RootState) => state.auth.role);
  if (role !== AdminRole.KIOSK_OWNER) return null;
  return <OwnerTourRunner />;
}
